import type React from "react";

import { colorsPalettes } from "@similarweb/styles";
import { AutocompleteItemWrapper, IconButton } from "@similarweb/ui-components";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import NoSearchResults from "app/pages/sales-intelligence/modules/company-autocomplete/components/NoSearchResults/NoSearchResults";
import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";

import { StyledRecipientsDropdownItem } from "./styles";

type Props = {
    data?: Contact[];
    search: string;
    onSelectContact: (value: Contact | null) => void;
};

export const RecipientsResults: React.FC<Props> = ({ data, search, onSelectContact }) => {
    const handleSelectContact = (contact: Contact) => {
        onSelectContact(contact);
        AIAgentTrackingService.email.trackSelectRecipient(
            `${contact?.name || ""}, ${contact?.title || ""}`,
            !search?.trim(),
        );
    };

    const handleAddNewContact = () => {
        onSelectContact({ name: search?.trim() });
        AIAgentTrackingService.email.trackAddNewRecipient(search?.trim());
    };

    if (search && Array.isArray(data) && data.length === 0) {
        return (
            <Stack
                alignItems="center"
                margin="0 0 16px"
                maxWidth="320px"
            >
                <NoSearchResults searchTerm={search} />
                <IconButton
                    iconName="add"
                    type="flat"
                    onClick={handleAddNewContact}
                >
                    {translate("si.pages.ai_agent_email.recipient.add")}
                </IconButton>
            </Stack>
        );
    }
    if (Array.isArray(data)) {
        return (
            <>
                {!search && data.length > 0 ? (
                    <Text
                        fontVariant="captionTitle"
                        color={colorsPalettes.carbon[250]}
                        padding="6px 16px"
                        display="block"
                    >
                        {translate("si.pages.ai_agent_email.recipient.suggestions")}
                    </Text>
                ) : null}
                {data.map((contact: { name: string; title: string }) => (
                    <AutocompleteItemWrapper
                        key={`${contact.name}-${contact.title}`}
                        onClick={() => {
                            handleSelectContact(contact);
                        }}
                    >
                        <StyledRecipientsDropdownItem>
                            <Text
                                fontVariant="input2"
                                color={colorsPalettes.carbon[500]}
                            >
                                {contact.name}
                            </Text>
                            <Text
                                fontVariant="listItem3"
                                color={colorsPalettes.carbon[300]}
                            >
                                {contact.title}
                            </Text>
                        </StyledRecipientsDropdownItem>
                    </AutocompleteItemWrapper>
                ))}
            </>
        );
    }
    return null;
};
